import React, { useState, useEffect } from "react";
import API from "../services/api";
import { Activity, FileText, CheckCircle, AlertTriangle, Clock, RefreshCw, Server, Search, ChevronDown, ChevronRight, HardDrive } from "lucide-react";
import { format, parseISO } from "date-fns";

export default function OCRMonitor() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDocId, setExpandedDocId] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchLogs = async () => {
    try {
      setLoading(true);
      // no trailing slash - FastAPI 307-redirects to localhost through nginx
      const res = await API.get("/ocr");
      setDocuments(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    const interval = setInterval(fetchLogs, 5000); // Auto-refresh every 5s
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    if (status === "parsed" || status === "success") return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
    if (status === "error") return "text-red-400 bg-red-500/10 border-red-500/20";
    return "text-blue-400 bg-blue-500/10 border-blue-500/20";
  };

  const filteredDocs = documents.filter(d => {
    if (filter === "error") return d.status === "error";
    if (filter === "processing") return d.status === "processing";
    if (filter === "parsed") return d.status === "parsed" || d.status === "success";
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Activity className="text-blue-500" />
            AI Parser & OCR Monitor
          </h1>
          <p className="text-slate-400 text-sm mt-1">Live feed of incoming documents and extraction status.</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Documents</option>
            <option value="processing">Processing</option>
            <option value="parsed">Successfully Parsed</option>
            <option value="error">Errors</option>
          </select>
          <button 
            onClick={fetchLogs}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl flex items-center gap-4">
          <div className="p-3 bg-blue-500/10 rounded-lg text-blue-400"><FileText size={24} /></div>
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Total Docs</p>
            <p className="text-2xl font-bold text-white">{documents.length}</p>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl flex items-center gap-4">
          <div className="p-3 bg-emerald-500/10 rounded-lg text-emerald-400"><CheckCircle size={24} /></div>
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Parsed</p>
            <p className="text-2xl font-bold text-white">{documents.filter(d => d.status === 'parsed').length}</p>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl flex items-center gap-4">
          <div className="p-3 bg-red-500/10 rounded-lg text-red-400"><AlertTriangle size={24} /></div>
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Errors</p>
            <p className="text-2xl font-bold text-white">{documents.filter(d => d.status === 'error').length}</p>
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 p-4 rounded-xl flex items-center gap-4">
          <div className="p-3 bg-indigo-500/10 rounded-lg text-indigo-400"><Server size={24} /></div>
          <div>
            <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">Parser Node</p>
            <p className="text-lg font-bold text-white">Online</p>
          </div>
        </div>
      </div>

      <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
        <div className="divide-y divide-slate-800/50">
          {filteredDocs.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Search className="mx-auto mb-3 opacity-20" size={48} />
              <p>No documents found matching the criteria.</p>
            </div>
          ) : (
            filteredDocs.map((doc) => (
              <div key={doc.id} className="flex flex-col">
                <div 
                  className="p-4 hover:bg-slate-900/50 transition-colors cursor-pointer flex items-center justify-between"
                  onClick={() => setExpandedDocId(expandedDocId === doc.id ? null : doc.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {expandedDocId === doc.id ? <ChevronDown className="text-slate-500" /> : <ChevronRight className="text-slate-500" />}
                    
                    <div className="flex flex-col">
                      <span className="font-semibold text-white">{doc.filename}</span>
                      <span className="text-xs text-slate-400 flex items-center gap-2 mt-1">
                        <Clock size={12} /> {doc.created_at ? format(parseISO(doc.created_at), "PP HH:mm:ss") : "Unknown"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="hidden md:flex flex-col items-end">
                      <span className="text-xs text-slate-500 uppercase font-semibold">Type</span>
                      <span className="text-sm text-slate-300">{doc.type || "IMAP Extract"}</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full border text-xs font-semibold capitalize ${getStatusColor(doc.status)}`}>
                      {doc.status}
                    </span>
                  </div>
                </div>
                
                {/* Expandable Logs Section */}
                {expandedDocId === doc.id && (
                  <div className="bg-slate-900 p-4 border-t border-slate-800/50 text-sm">
                    {doc.error && (
                      <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 flex gap-2">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <div>
                          <p className="font-semibold text-xs uppercase tracking-wider mb-1">Critical Error</p>
                          <p className="font-mono text-xs">{doc.error}</p>
                        </div>
                      </div>
                    )}
                    
                    <h4 className="font-semibold text-slate-400 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                      <HardDrive size={14} /> Extraction Logs
                    </h4>
                    
                    <div className="space-y-2 font-mono text-xs max-h-64 overflow-y-auto">
                      {doc.logs && doc.logs.length > 0 ? (
                        doc.logs.map((log, idx) => (
                          <div key={idx} className="flex gap-4 p-2 rounded hover:bg-slate-800/50">
                            <span className="text-slate-500 shrink-0">{format(parseISO(log.time), "HH:mm:ss")}</span>
                            <span className={`shrink-0 w-16 uppercase ${log.level === 'error' ? 'text-red-400' : log.level === 'warning' ? 'text-orange-400' : 'text-blue-400'}`}>
                              [{log.level}]
                            </span>
                            <span className="text-slate-300">{log.message}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 italic p-2">No detailed logs available.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
